import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fetchBranding } from '@/shared/branding/branding.api';
import { applyBranding, cacheBranding, cachedBranding } from '@/shared/branding/apply-branding';
import type { Branding } from '@/shared/branding/branding.types';

/** What the application looks like before the server has answered, or if it never does. */
const FALLBACK: Branding = {
  siteName: 'Talento',
  brandColor: '#9c8a56',
  // Empty: the compiled `@theme` values already are this palette, so applying nothing
  // leaves the design system exactly as it ships.
  palette: {},
  logoUrl: null,
};

export const useBrandingStore = defineStore('branding', () => {
  const branding = ref<Branding>(cachedBranding() ?? FALLBACK);

  /** Applied before mount, which is what removes the flash of the default theme. */
  function applyCached(): void {
    applyBranding(branding.value);
  }

  function adopt(next: Branding): void {
    branding.value = next;
    applyBranding(next);
    cacheBranding(next);
  }

  /**
   * Revalidation runs in parallel with mounting. A failure is not surfaced: the shell has
   * already painted with the cached values, and an error toast about the logo would be
   * noise on top of whatever real problem caused the request to fail.
   */
  async function revalidate(): Promise<void> {
    try {
      adopt(await fetchBranding());
    } catch {
      // Keep whatever is on screen.
    }
  }

  return { branding, applyCached, adopt, revalidate };
});
